// B.4d typecheck-zero: File-local types + Props interface. See docs/plans/archive/2026-04-12-typecheck-zero.
import {
  useState,
  useRef,
  useEffect,
  useMemo,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
  type RefObject,
  type FocusEvent as ReactFocusEvent,
  type ChangeEvent as ReactChangeEvent,
  type MutableRefObject,
} from 'react';
import SearchableSelectRaw from '@/components/ui/SearchableSelect';
import SmartSelectRaw from '@/components/ui/SmartSelect';
import ValorInputControlledRaw from '@/components/ui/ValorInputControlled';
import CepInputRaw from '@/components/ui/CepInput';
import SwitchRaw from '@/components/ui/Switch';
import FieldsetCardRaw from '@/components/ui/FieldsetCard';
import { formatMoney, formatDate } from '@/utils/formatters';
import { Calendar } from 'lucide-react';

// Single-step Raw-alias casts at import boundary: sibling components are still untyped/partially-typed
// JS-style. Cast to permissive ComponentType so FormFieldsSection can forward richer prop shapes
// without modifying those files. Follow-up: type each sibling in future Phase B sweeps.
const SearchableSelect = SearchableSelectRaw as unknown as ComponentType<Record<string, unknown>>;
const SmartSelect = SmartSelectRaw as unknown as ComponentType<Record<string, unknown>>;
const ValorInputControlled = ValorInputControlledRaw as unknown as ComponentType<Record<string, unknown>>;
const CepInput = CepInputRaw as unknown as ComponentType<Record<string, unknown>>;
const Switch = SwitchRaw as unknown as ComponentType<Record<string, unknown>>;
const FieldsetCard = FieldsetCardRaw as unknown as ComponentType<
  Record<string, unknown> & { children?: ReactNode }
>;

// ========== Companion types (file-local) ==========

type FormValues = Record<string, unknown>;
type FormLookupOption = Record<string, unknown> & {
  id?: string | number | null;
  value?: string | number | null;
  label?: string;
  nome?: string;
  codigo?: string;
};
type FormLookups = Record<string, FormLookupOption[] | undefined>;
type FormErrors = Record<string, string | undefined>;
type FormConfig = Record<string, unknown>;

interface DrawerField {
  field: string;
  label?: string | ((values: FormValues) => string);
  type?: string;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
  hidden?: boolean;
  hideInView?: boolean;
  hideInEdit?: boolean;
  showWhen?: (values: FormValues) => boolean;
  // Lookup / options
  lookupKey?: string;
  options?: FormLookupOption[];
  searchable?: boolean;
  filterFn?: (option: FormLookupOption, config: FormConfig) => boolean;
  // Layout / styling
  row?: number;
  width?: string;
  minWidth?: string;
  inline?: boolean;
  textAlign?: 'left' | 'right' | 'center';
  wrapperStyle?: CSSProperties;
  labelColor?: string;
  bold?: boolean;
  valueBold?: boolean;
  large?: boolean;
  valueColor?: string;
  // Numeric / currency
  min?: number;
  max?: number;
  step?: number;
  compact?: boolean;
  showPrefix?: boolean;
  showLabelSuffix?: boolean;
  emptyAsNull?: boolean;
  // Date
  shortYear?: boolean;
  // Textarea
  rows?: number;
  // Checkbox
  checkboxLabel?: string;
  // Inline content after the label, edit mode only (e.g. an origin tag)
  labelAdornment?: ReactNode;
  // Toggle (rateio pattern)
  toggleField?: string;
  toggleLabel?: string;
  // CEP
  cidadesLookupKey?: string;
  fieldMapping?: Record<string, string>;
  focusAfterFill?: string;
  onAddressFound?: (address: Record<string, unknown>) => void;
  // Smart-select
  smartSelectProps?: Record<string, unknown>;
  // Custom formatter
  format?: (value: unknown) => string;
  // Custom render
  renderView?: (
    value: unknown,
    values: FormValues,
    lookups: FormLookups,
    config: FormConfig,
  ) => ReactNode;
  renderEdit?: (
    value: unknown,
    onChange: (newValue: unknown) => void,
    values: FormValues,
    lookups: FormLookups,
    config: FormConfig,
  ) => ReactNode;
  // Allow forward-compat untyped extras without breaking consumers.
  [key: string]: unknown;
}

interface DrawerRow {
  display?: string;
  columns?: string;
  gap?: string;
  marginBottom?: string;
  style?: CSSProperties;
  fields?: string[];
}

interface DrawerCard {
  id?: string | number;
  title?: string;
  style?: CSSProperties;
  flex?: string | number;
  display?: string;
  direction?: 'row' | 'column';
  gap?: string;
  rows?: DrawerRow[];
  fields?: string[];
}

type FieldChangeHandler = (field: string, value: unknown) => void;

export interface FormFieldsSectionProps {
  fields?: DrawerField[];
  values?: FormValues;
  onChange: FieldChangeHandler;
  mode?: 'view' | 'edit';
  lookups?: FormLookups;
  errors?: FormErrors;
  config?: FormConfig;
  cards?: DrawerCard[];
  autoFocus?: boolean;
}

// ========== ShortDateInput ==========

interface ShortDateInputProps {
  value?: string | null;
  onChange: (iso: string) => void;
  disabled?: boolean;
  style?: CSSProperties;
  onFocus?: (e: ReactFocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: ReactFocusEvent<HTMLInputElement>) => void;
  inputRef?: RefObject<HTMLInputElement | null> | MutableRefObject<HTMLInputElement | null> | null;
}

/**
 * ShortDateInput - Masked date input that displays/accepts DD/MM/YY
 * Stores value internally as YYYY-MM-DD.
 * Includes a calendar icon that opens the native date picker.
 */
function ShortDateInput({
  value,
  onChange,
  disabled,
  style,
  onFocus,
  onBlur,
  inputRef,
}: ShortDateInputProps) {
  const pickerRef = useRef<HTMLInputElement | null>(null);

  const toDisplay = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const parts = iso.split('-');
    if (parts.length !== 3) return '';
    return `${parts[2]}/${parts[1]}/${parts[0].slice(2)}`;
  };

  const [display, setDisplay] = useState<string>(() => toDisplay(value));

  useEffect(() => {
    setDisplay(toDisplay(value));
  }, [value]);

  const handleChange = (e: ReactChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 6) raw = raw.slice(0, 6);

    let formatted = raw;
    if (raw.length >= 5) {
      formatted = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4)}`;
    } else if (raw.length >= 3) {
      formatted = `${raw.slice(0, 2)}/${raw.slice(2)}`;
    }
    setDisplay(formatted);

    if (raw.length === 6) {
      const dd = raw.slice(0, 2);
      const mm = raw.slice(2, 4);
      const yy = raw.slice(4, 6);
      const yyyy = parseInt(yy, 10) > 50 ? `19${yy}` : `20${yy}`;
      onChange(`${yyyy}-${mm}-${dd}`);
    }
  };

  const handlePickerChange = (e: ReactChangeEvent<HTMLInputElement>) => {
    const iso = e.target.value;
    if (iso) {
      setDisplay(toDisplay(iso));
      onChange(iso);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input
        ref={inputRef ?? undefined}
        type="text"
        inputMode="numeric"
        value={display}
        onChange={handleChange}
        placeholder="DD/MM/AA"
        disabled={disabled}
        style={{ ...style, paddingRight: '28px' }}
        onFocus={onFocus}
        onBlur={onBlur}
        maxLength={8}
      />
      {/* Hidden native date picker */}
      <input
        ref={pickerRef}
        type="date"
        value={value || ''}
        onChange={handlePickerChange}
        disabled={disabled}
        tabIndex={-1}
        style={{
          position: 'absolute',
          opacity: 0,
          width: 0,
          height: 0,
          right: 0,
          pointerEvents: 'none',
        }}
      />
      <Calendar
        size={14}
        style={{
          position: 'absolute',
          right: '8px',
          color: disabled ? '#ccc' : '#888',
          cursor: disabled ? 'default' : 'pointer',
        }}
        onClick={() => {
          if (!disabled && pickerRef.current) {
            // showPicker() is HTMLInputElement method (lib.dom newer), guard for older TS lib.
            const el = pickerRef.current as HTMLInputElement & { showPicker?: () => void };
            el.showPicker?.();
          }
        }}
      />
    </div>
  );
}

/**
 * FormFieldsSection - Renderiza campos de formulário dinamicamente
 *
 * Suporta layouts avançados do LancamentoDrawer:
 * - Grids configuráveis (1fr 140px, 2fr 1fr 1fr, etc.)
 * - Cards com estilos padrão
 * - Campos com toggles (rateio)
 * - ValorInputControlled para currency
 * - Campos calculados (readonly)
 */
export default function FormFieldsSection({
  fields = [],
  values = {},
  onChange,
  mode = 'view',
  lookups = {},
  errors = {},
  config = {},
  cards = [],
  autoFocus = true,
}: FormFieldsSectionProps) {
  const isEditing = mode === 'edit';
  const firstInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Autofocus no primeiro campo quando entra em modo de edição
  useEffect(() => {
    if (isEditing && autoFocus) {
      // Delay para garantir que o drawer terminou de abrir e o ref foi atribuído
      const timer = setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isEditing, autoFocus]);

  // ========== Estilos Padrão (extraídos do LancamentoDrawer) ==========
  const styles: Record<string, CSSProperties> = {
    card: {
      background: 'white',
      border: '1px solid var(--panel-border)',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      padding: '12px 16px',
      marginBottom: '12px',
    },
    label: {
      fontSize: '12px',
      color: 'var(--muted)',
      marginBottom: '4px',
      display: 'block',
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid var(--panel-border)',
      borderRadius: '6px',
      fontSize: '14px',
      background: 'white',
      outline: 'none',
      transition: 'border-color 0.2s ease',
    },
    valueInput: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid var(--panel-border)',
      borderRadius: '6px',
      fontSize: '14px',
      background: 'white',
      textAlign: 'right',
    },
    viewValue: {
      fontSize: '14px',
      color: '#374151',
    },
    viewValueBold: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#374151',
    },
    viewValueLarge: {
      fontSize: '15px',
      fontWeight: 600,
      color: '#1f2937',
    },
  };

  // ========== Formatadores ==========
  const formatValue = (field: DrawerField, value: unknown): ReactNode => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof field.format === 'function') {
      return field.format(value);
    }

    switch (field.type) {
      case 'currency':
      case 'money':
        return formatMoney(value as number);

      case 'date':
        return formatDate(value as string);

      case 'datetime':
        if (!value) return '-';
        return new Date(value as string).toLocaleString('pt-BR');

      case 'select':
      case 'buttonGroup': {
        if (field.lookupKey && lookups[field.lookupKey]) {
          const option = (lookups[field.lookupKey] ?? []).find(
            (o: FormLookupOption) => o.id === value || o.value === value,
          );
          // Suporta formato "codigo - nome"
          if (option?.codigo && option?.nome) {
            return `${option.codigo} - ${option.nome}`;
          }
          return option?.nome || option?.label || String(value);
        }
        if (field.options) {
          const option = field.options.find(
            (o: FormLookupOption) => o.value === value || o.id === value,
          );
          return option?.label || String(value);
        }
        return String(value);
      }

      case 'smart-select':
        // UUID puro — mostra o ID truncado (SmartSelect resolve no edit mode)
        if (typeof value === 'string' && value.length > 20) {
          return value.substring(0, 8) + '...';
        }
        if (value && typeof value === 'object' && 'label' in value) {
          return String((value as { label?: unknown }).label ?? '-');
        }
        return String(value || '-');

      case 'boolean':
        return value ? 'Sim' : 'Não';

      case 'percent':
        return `${parseFloat(String(value)).toFixed(2)}%`;

      default:
        return String(value);
    }
  };

  // ========== Renderizadores de Campo ==========

  // Renderiza valor em modo VIEW
  const renderViewField = (field: DrawerField): ReactNode => {
    const value = values[field.field];

    // Campo customizado
    if (field.renderView) {
      return field.renderView(value, values, lookups, config);
    }

    // Smart-select em VIEW: renderizar SmartSelect disabled (auto-resolve UUID → label)
    if (field.type === 'smart-select') {
      if (!value) {
        return <div style={styles.viewValue}>—</div>;
      }
      return (
        <SmartSelect
          value={value}
          onChange={() => {}}
          isDisabled={true}
          {...(field.smartSelectProps || {})}
        />
      );
    }

    // Campos booleanos em modo VIEW: mostrar switch desabilitado
    if (field.type === 'boolean' || field.type === 'toggle') {
      return <Switch checked={!!value} onChange={() => {}} disabled={true} size="small" />;
    }

    // Estilo baseado nas opções do campo
    let valueStyle: CSSProperties = { ...styles.viewValue };
    if (field.bold || field.valueBold) valueStyle.fontWeight = 600;
    if (field.large) valueStyle = { ...styles.viewValueLarge };
    if (field.valueColor) valueStyle.color = field.valueColor;
    if (field.textAlign === 'right') valueStyle.textAlign = 'right';

    // Renderizar valor formatado
    return <div style={valueStyle}>{formatValue(field, value)}</div>;
  };

  // Calcula qual é o primeiro campo focável (determinístico)
  const firstFocusableField = useMemo<string | null>(() => {
    // Busca o primeiro campo não-oculto, não-disabled e focável
    const focusableTypes = [
      'text',
      'email',
      'tel',
      'number',
      'date',
      'datetime',
      'textarea',
      'select',
      'smart-select',
      'cep',
      'currency',
    ];

    // Helper para verificar se campo deve ser exibido
    const isFieldVisible = (field: DrawerField): boolean => {
      if (field.hidden) return false;
      if (field.showWhen && typeof field.showWhen === 'function') {
        if (!field.showWhen(values)) return false;
      }
      return true;
    };

    // Se há cards, buscar na ordem dos cards
    if (cards && cards.length > 0) {
      for (const card of cards) {
        const cardFields: string[] = card.rows
          ? card.rows.flatMap((row) => row.fields || [])
          : card.fields || [];

        for (const fieldId of cardFields) {
          const field = fields.find((f) => f.field === fieldId);
          if (
            field &&
            isFieldVisible(field) &&
            !field.disabled &&
            focusableTypes.includes(field.type || 'text')
          ) {
            return field.field;
          }
        }
      }
    }

    // Fallback: buscar na lista de fields
    const found = fields.find(
      (f) => isFieldVisible(f) && !f.disabled && focusableTypes.includes(f.type || 'text'),
    );
    return found?.field || null;
  }, [fields, cards, values]);

  // Renderiza campo em modo EDIT
  const renderEditField = (field: DrawerField, isFirstFocusable = false): ReactNode => {
    const value = values[field.field];
    const error = errors[field.field];
    const hasError = !!error;

    // Campo customizado
    if (field.renderEdit) {
      return field.renderEdit(
        value,
        (newValue: unknown) => onChange(field.field, newValue),
        values,
        lookups,
        config,
      );
    }

    // Estilo base com erro
    const baseInputStyle: CSSProperties = {
      ...styles.input,
      borderColor: hasError ? 'var(--color-danger)' : 'var(--panel-border)',
      background: field.disabled ? '#f9fafb' : 'white',
    };

    // Handlers de foco
    const handleFocus = (e: ReactFocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      e.target.style.borderColor = 'var(--primary)';
    };
    const handleBlur = (e: ReactFocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      e.target.style.borderColor = hasError ? 'var(--color-danger)' : 'var(--panel-border)';
    };

    // Ref para o primeiro campo focável
    // Cast: firstInputRef is a union ref (input|textarea); per-branch element type resolves at JSX
    // boundary. Using typed ref unions in React breaks variance — narrow at assignment site.
    const inputRef = isFirstFocusable
      ? (firstInputRef as unknown as RefObject<HTMLInputElement | null>)
      : null;
    const textareaRef = isFirstFocusable
      ? (firstInputRef as unknown as RefObject<HTMLTextAreaElement | null>)
      : null;

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
        return (
          <input
            ref={inputRef}
            type={field.type}
            name={field.field}
            value={(value as string | undefined) || ''}
            onChange={(e) => onChange(field.field, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.disabled}
            style={baseInputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        );

      case 'number':
        return (
          <input
            ref={inputRef}
            type="number"
            value={(value as number | string | undefined) || ''}
            onChange={(e) => onChange(field.field, e.target.value ? parseFloat(e.target.value) : '')}
            placeholder={field.placeholder}
            disabled={field.disabled}
            min={field.min}
            max={field.max}
            step={field.step}
            style={{ ...baseInputStyle, textAlign: field.textAlign || 'left' }}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        );

      case 'currency':
        // Usa ValorInputControlled para campos de valor monetário
        // NÃO sobrescrever estilos padrão do componente (fontSize: 13px, sem monospace)
        return (
          <ValorInputControlled
            value={value}
            onChange={(numValue: unknown) => onChange(field.field, numValue)}
            emptyAsNull={field.emptyAsNull}
            onEmpty={field.emptyAsNull ? () => onChange(field.field, null) : undefined}
            style={field.compact ? { padding: '6px 10px', width: field.width || '100px' } : {}}
            hasError={hasError}
            disabled={field.disabled}
            showPrefix={field.showPrefix}
          />
        );

      case 'cep': {
        // Usa CepInput com busca automática de endereço via ViaCEP
        // O componente preenche automaticamente endereco, bairro e cidade
        // Busca a lista de cidades no lookups (padrão: 'cidades')
        const cidadesLookupKey = field.cidadesLookupKey || 'cidades';
        const cidadesLookup = lookups[cidadesLookupKey] || [];

        return (
          <CepInput
            value={(value as string | undefined) || ''}
            onChange={(cep: unknown) => onChange(field.field, cep)}
            onFormChange={onChange}
            cidadesLookup={cidadesLookup}
            fieldMapping={field.fieldMapping}
            focusAfterFill={field.focusAfterFill}
            onAddressFound={field.onAddressFound}
            placeholder={field.placeholder || '00000-000'}
            disabled={field.disabled}
            style={baseInputStyle}
          />
        );
      }

      case 'date':
        if (field.shortYear) {
          return (
            <ShortDateInput
              inputRef={inputRef}
              value={(value as string | undefined) || ''}
              onChange={(v: string) => onChange(field.field, v)}
              disabled={field.disabled}
              style={baseInputStyle}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          );
        }
        return (
          <input
            ref={inputRef}
            type="date"
            value={(value as string | undefined) || ''}
            onChange={(e) => onChange(field.field, e.target.value)}
            disabled={field.disabled}
            style={baseInputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        );

      case 'datetime':
        return (
          <input
            ref={inputRef}
            type="datetime-local"
            value={(value as string | undefined) || ''}
            onChange={(e) => onChange(field.field, e.target.value)}
            disabled={field.disabled}
            style={baseInputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        );

      case 'textarea':
        return (
          <textarea
            ref={textareaRef}
            value={(value as string | undefined) || ''}
            onChange={(e) => onChange(field.field, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.disabled}
            rows={field.rows || 3}
            style={{ ...baseInputStyle, resize: 'vertical' }}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        );

      case 'smart-select':
        // SmartSelect com auto-detecção (entity-based ou manual)
        // formData armazena UUID puro; SmartSelect resolve label automaticamente
        // isClearable default = true (SmartSelect default); required valida no save
        return (
          <SmartSelect
            value={value || null}
            onChange={(opt: FormLookupOption | null) =>
              onChange(field.field, opt?.value || null)
            }
            placeholder={field.placeholder || 'Selecione...'}
            isDisabled={field.disabled}
            {...(field.smartSelectProps || {})}
          />
        );

      case 'select': {
        // Usar SearchableSelect para selects com muitas opções
        const lookupList = field.lookupKey ? lookups[field.lookupKey] : undefined;
        if (field.searchable || (lookupList && lookupList.length > 10)) {
          const options: FormLookupOption[] = lookupList
            ? lookupList
                .filter((o: FormLookupOption) => !field.filterFn || field.filterFn(o, config))
                .map((o: FormLookupOption) => ({
                  value: (o.id ?? o.value) as string | number | null,
                  label:
                    o.codigo && o.nome ? `${o.codigo} - ${o.nome}` : (o.nome || o.label || ''),
                }))
            : field.options?.map((o: FormLookupOption) => ({
                value: (o.value ?? o.id) as string | number | null,
                label: o.label ?? '',
              })) || [];

          const selectedOption = options.find((o: FormLookupOption) => o.value === value);

          return (
            <SearchableSelect
              options={options}
              value={selectedOption || null}
              onChange={(opt: FormLookupOption | null) =>
                onChange(field.field, opt?.value || null)
              }
              placeholder={field.placeholder || 'Selecione...'}
              isClearable={!field.required}
              isDisabled={field.disabled}
            />
          );
        }

        // Select simples
        const selectOptions: FormLookupOption[] = lookupList
          ? lookupList.filter(
              (o: FormLookupOption) => !field.filterFn || field.filterFn(o, config),
            )
          : field.options || [];

        return (
          <select
            value={(value as string | number | undefined) ?? ''}
            onChange={(e) => onChange(field.field, e.target.value || null)}
            disabled={field.disabled}
            style={baseInputStyle}
          >
            <option value="">{field.placeholder || 'Selecione...'}</option>
            {selectOptions.map((opt: FormLookupOption) => {
              const optValue = (opt.id ?? opt.value) as string | number;
              return (
                <option key={String(optValue)} value={optValue}>
                  {opt.codigo && opt.nome
                    ? `${opt.codigo} - ${opt.nome}`
                    : opt.nome || opt.label}
                </option>
              );
            })}
          </select>
        );
      }

      case 'boolean':
      case 'toggle':
        // Switch padrão para campos booleanos - usa componente Switch padronizado
        return (
          <Switch
            checked={!!value}
            onChange={(newValue: unknown) => onChange(field.field, newValue)}
            disabled={field.disabled}
            size="small"
          />
        );

      case 'checkbox':
        // Checkbox tradicional (usar apenas quando explicitamente necessário)
        return (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: field.disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(field.field, e.target.checked)}
              disabled={field.disabled}
              style={{ width: '16px', height: '16px' }}
            />
            <span style={{ fontSize: '14px', color: '#374151' }}>
              {field.checkboxLabel || getLabel(field)}
            </span>
          </label>
        );

      case 'buttonGroup': {
        // Grupo de botões com seleção única (estilo chips/toggle buttons)
        const buttonGroupOptions: FormLookupOption[] = field.options || [];
        return (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            {buttonGroupOptions.map((opt: FormLookupOption) => {
              const isSelected = value === opt.value;
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => !field.disabled && onChange(field.field, opt.value)}
                  disabled={field.disabled}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? 'white' : '#374151',
                    background: isSelected ? '#154680' : '#f3f4f6',
                    border: isSelected
                      ? '1px solid #154680'
                      : '1px solid var(--panel-border)',
                    borderRadius: '6px',
                    cursor: field.disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s ease',
                    opacity: field.disabled ? 0.6 : 1,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        );
      }

      case 'readonly':
        // Campo somente leitura (mesmo visual em view e edit)
        return (
          <div
            style={{
              ...styles.input,
              background: '#f9fafb',
              color: 'var(--muted)',
              ...(field.textAlign === 'right' ? { textAlign: 'right' } : {}),
            }}
          >
            {formatValue(field, value)}
          </div>
        );

      default:
        return (
          <input
            ref={inputRef}
            type="text"
            value={(value as string | undefined) || ''}
            onChange={(e) => onChange(field.field, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.disabled}
            style={baseInputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        );
    }
  };

  // ========== Renderização Principal ==========

  // Helper para renderizar wrapper do campo
  function renderFieldWrapper(field: DrawerField): ReactNode {
    // Não renderizar campos ocultos
    if (field.hidden) return null;
    if (field.hideInView && !isEditing) return null;
    if (field.hideInEdit && isEditing) return null;
    // Suporte para showWhen - exibe campo apenas quando condição é verdadeira
    if (field.showWhen && typeof field.showWhen === 'function') {
      if (!field.showWhen(values)) return null;
    }

    const error = errors[field.field];

    // Determina se este é o primeiro campo focável (usa o cálculo determinístico)
    const isFirstFocusable = isEditing && field.field === firstFocusableField;

    // Layout inline (label e valor na mesma linha)
    if (field.inline) {
      return (
        <div
          key={field.field}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            ...(field.wrapperStyle || {}),
          }}
        >
          {/* Label com cor opcional */}
          <span
            style={{
              fontSize: '11px',
              color: field.labelColor || 'var(--muted)',
            }}
          >
            {getLabel(field)}
          </span>

          {/* Valor/Input */}
          <div style={{ minWidth: field.minWidth || 'auto' }}>
            {isEditing ? renderEditField(field, isFirstFocusable) : renderViewField(field)}
          </div>
        </div>
      );
    }

    // Layout padrão (label em cima)
    return (
      <div
        key={field.field}
        style={{
          minWidth: field.minWidth || 'auto',
          textAlign: field.textAlign || 'left',
          ...(field.wrapperStyle || {}),
        }}
      >
        {/* Label com toggle opcional - checkbox e toggle têm tratamento especial */}
        {field.type !== 'checkbox' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '4px',
              minHeight: field.toggleField ? '20px' : 'auto',
            }}
          >
            <label
              style={{
                ...styles.label,
                marginBottom: 0,
                color: field.labelColor || 'var(--muted)',
              }}
            >
              {getLabel(field)}
              {field.required && isEditing && <span className="required-asterisk">*</span>}
            </label>

            {field.labelAdornment && isEditing && field.labelAdornment}

            {/* Toggle inline (padrão do rateio) */}
            {field.toggleField && isEditing && (
              <>
                <button
                  type="button"
                  onClick={() =>
                    onChange(field.toggleField as string, !values[field.toggleField as string])
                  }
                  style={{
                    position: 'relative',
                    width: '36px',
                    height: '20px',
                    borderRadius: '10px',
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: values[field.toggleField as string]
                      ? 'var(--primary)'
                      : '#d1d5db',
                    transition: 'background-color 0.2s ease',
                    padding: 0,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: values[field.toggleField as string] ? '18px' : '2px',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'left 0.2s ease',
                    }}
                  />
                </button>
                <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                  {field.toggleLabel || 'Rateio'}
                </span>
              </>
            )}
          </div>
        )}

        {/* Campo */}
        {isEditing ? renderEditField(field, isFirstFocusable) : renderViewField(field)}

        {/* Erro */}
        {error && (
          <p
            style={{
              margin: '4px 0 0',
              fontSize: '12px',
              color: 'var(--danger)',
            }}
          >
            {error}
          </p>
        )}

        {/* Hint */}
        {field.hint && isEditing && (
          <p
            style={{
              margin: '4px 0 0',
              fontSize: '11px',
              color: 'var(--muted)',
            }}
          >
            {field.hint}
          </p>
        )}
      </div>
    );
  }

  // Helper para obter label (suporta config dinâmico, funções e sufixo R$ para currency)
  function getLabel(field: DrawerField): string {
    // Verifica se há label personalizado na config
    const configLabelKey = `label${field.field.charAt(0).toUpperCase() + field.field.slice(1)}`;
    const configLabel = config[configLabelKey];
    let label: string | ((values: FormValues) => string) =
      (typeof configLabel === 'string' || typeof configLabel === 'function'
        ? (configLabel as string | ((values: FormValues) => string))
        : undefined) ||
      field.label ||
      field.field;

    // Suporte para label dinâmico via função
    if (typeof label === 'function') {
      label = label(values);
    }

    // Adiciona (R$) para campos currency se showLabelSuffix não for explicitamente false
    if (field.type === 'currency' && field.showLabelSuffix !== false) {
      label = `${label} (R$)`;
    }

    return label as string;
  }

  // Se há configuração de cards, renderizar com agrupamento
  if (cards && cards.length > 0) {
    // Helper para renderizar conteúdo do card (rows ou fields)
    const renderCardContent = (card: DrawerCard): ReactNode => (
      <>
        {/* Renderizar rows do card */}
        {card.rows?.map((row: DrawerRow, rowIndex: number) => {
          // Verificar se algum campo da row é visível
          const visibleFields =
            row.fields?.filter((fieldId: string) => {
              const field = fields.find((f) => f.field === fieldId);
              if (!field) return false;
              if (field.hidden) return false;
              if (field.hideInView && !isEditing) return false;
              if (field.hideInEdit && isEditing) return false;
              if (field.showWhen && typeof field.showWhen === 'function') {
                if (!field.showWhen(values)) return false;
              }
              return true;
            }) || [];

          // Pular row se não tem campos visíveis
          if (visibleFields.length === 0) return null;

          return (
            <div
              key={rowIndex}
              style={{
                display: row.display || 'grid',
                gridTemplateColumns: row.columns || '1fr',
                gap: row.gap || '16px',
                marginBottom:
                  rowIndex < (card.rows?.length ?? 0) - 1
                    ? row.marginBottom || '16px'
                    : 0,
                ...(row.style || {}),
              }}
            >
              {row.fields?.map((fieldId: string) => {
                const field = fields.find((f) => f.field === fieldId);
                if (!field) return null;
                return renderFieldWrapper(field);
              })}
            </div>
          );
        })}

        {/* Campos sem rows (layout simples) */}
        {card.fields && !card.rows && (
          <div
            style={{
              display: card.display || 'flex',
              flexDirection: card.direction || 'column',
              gap: card.gap || '8px',
            }}
          >
            {card.fields.map((fieldId: string) => {
              const field = fields.find((f) => f.field === fieldId);
              if (!field) return null;
              return renderFieldWrapper(field);
            })}
          </div>
        )}
      </>
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {cards.map((card: DrawerCard, cardIndex: number) => {
          // Se card tem título, usar FieldsetCard (estilo fieldset/legend)
          if (card.title) {
            return (
              <FieldsetCard
                key={card.id ?? cardIndex}
                title={card.title}
                style={{
                  ...(card.style || {}),
                  ...(card.flex ? { flex: card.flex, marginBottom: 0 } : { marginBottom: 0 }),
                }}
              >
                {renderCardContent(card)}
              </FieldsetCard>
            );
          }

          // Sem título: usar div padrão
          return (
            <div
              key={card.id ?? cardIndex}
              style={{
                ...styles.card,
                ...(card.style || {}),
                ...(card.flex ? { flex: card.flex, marginBottom: 0 } : {}),
              }}
            >
              {renderCardContent(card)}
            </div>
          );
        })}
      </div>
    );
  }

  // Renderização padrão (sem cards)
  // Agrupar campos por row (se especificado)
  const groupedFields = fields.reduce<DrawerField[][]>((acc, field) => {
    const row = field.row ?? acc.length;
    if (!acc[row]) acc[row] = [];
    acc[row].push(field);
    return acc;
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {groupedFields.map((rowFields: DrawerField[], rowIndex: number) => (
        <div
          key={rowIndex}
          style={{
            display: 'grid',
            gridTemplateColumns:
              rowFields.length > 1
                ? rowFields.map((f: DrawerField) => f.width || '1fr').join(' ')
                : '1fr',
            gap: '16px',
          }}
        >
          {rowFields.map((field: DrawerField) => renderFieldWrapper(field))}
        </div>
      ))}
    </div>
  );
}

/**
 * Exemplo de uso avançado (padrão LancamentoDrawer):
 *
 * const fields = [
 *   // Card 1: Informações Básicas
 *   { field: 'parte_id', label: 'Cliente', type: 'select', lookupKey: 'partes', searchable: true, required: true, large: true },
 *   { field: 'data_vencimento', label: 'Vencimento', type: 'date', required: true, bold: true, textAlign: 'right' },
 *   { field: 'descricao', label: 'Descrição', type: 'text', required: true },
 *   { field: 'valor_original', label: 'Valor (R$)', type: 'currency', required: true, bold: true, textAlign: 'right' },
 *   { field: 'categoria_id', label: 'Categoria', type: 'select', lookupKey: 'categorias', searchable: true, toggleField: 'habilitar_rateio_categoria', toggleLabel: 'Rateio' },
 *   { field: 'valor_original', label: 'Valor Original (R$)', type: 'currency', inline: true },
 *   { field: 'desconto', label: 'Desconto (R$)', type: 'currency', inline: true, compact: true },
 * ];
 */
